import { Link } from '@tanstack/react-router';
import { AnimatePresence, motion, type Variants } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { LanguageSwitcher } from '@components/ui/LanguageSwitcher';
import { SocialLinks } from '@components/ui/SocialLinks';
import { getNavigation } from '@content';
import { useAuthSession } from '@hooks/use-commerce';
import { useLocale } from '@hooks/use-locale';
import { easings } from '@lib/motion-presets';
import { cn } from '@lib/theme';
import { useMenuContext } from './MenuContext';
import NavigationItem from './NavigationItem';

const menuVariants: Variants = {
  closed: {
    opacity: 0,
    scale: 0.985,
    y: 8,
    transition: {
      duration: 0.26,
      ease: easings.smooth,
    },
  },
  open: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.34,
      ease: easings.smooth,
      staggerChildren: 0.06,
    },
  },
};

const itemVariants: Variants = {
  closed: { opacity: 0, y: 24 },
  open: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.42,
      ease: easings.smooth,
    },
  },
  exit: {
    opacity: 0,
    y: 20,
    transition: {
      duration: 0.22,
      ease: easings.smooth,
    },
  },
};

export default function FullScreenMenu() {
  const { isOpen, closeMenu } = useMenuContext();
  const sessionQuery = useAuthSession();
  const { locale, localizeHref } = useLocale();
  const navigationContent = getNavigation(locale);
  const isTamil = locale === 'ta';
  const priorityMenuItems = navigationContent.menu.slice(0, 4);
  const secondaryMenuItems = navigationContent.menu.slice(4);
  const [showSecondaryMenu, setShowSecondaryMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const moreLinksContainerRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLAnchorElement>(null);

  const handleCloseMenu = useCallback(() => {
    setShowSecondaryMenu(false);
    closeMenu();
  }, [closeMenu]);

  const handleToggleSecondaryMenu = useCallback(() => {
    setShowSecondaryMenu((prev) => {
      const next = !prev;
      if (next) {
        requestAnimationFrame(() => {
          moreLinksContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
      return next;
    });
  }, []);

  // Handle keyboard navigation and trap focus within menu
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Focus the first menu item when opened
    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    } else {
      const firstFocusable = menuRef.current?.querySelector<HTMLElement>(
        'a[href], button, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    }

    // Handle escape key to close menu
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseMenu();
      }

      // Trap focus within the menu when it's open
      if (e.key === 'Tab' && menuRef.current) {
        const focusableElements = menuRef.current.querySelectorAll('a[href], button, [tabindex]:not([tabindex="-1"])');
        if (!focusableElements.length) {
          return;
        }

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey && document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleCloseMenu]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          id="fullscreen-menu"
          ref={menuRef}
          className="bg-background fixed inset-0 z-110 flex min-h-dvh w-full items-start justify-center overflow-y-auto px-5 pt-24 pb-12 sm:px-8 sm:pt-28"
          initial="closed"
          animate="open"
          exit="closed"
          variants={menuVariants}
          role="dialog"
          aria-modal="true"
          aria-label={isTamil ? 'முதன்மை வழிசெலுத்தல்' : 'Main navigation'}
        >
          <button
            type="button"
            onClick={handleCloseMenu}
            className="border-border/60 text-foreground hover:border-brand-peacock/60 hover:text-brand-peacock focus-visible:ring-ring focus-visible:ring-offset-background absolute top-4 right-4 inline-flex min-h-11 items-center rounded-full border px-4 py-2 text-xs font-semibold tracking-[0.18em] uppercase transition-colors focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {isTamil ? 'மூடு' : 'Close'}
          </button>
          <nav
            className={cn(
              'flex w-full max-w-3xl flex-col items-center justify-start text-center',
              isTamil ? 'gap-4 sm:gap-5' : 'gap-5 sm:gap-7'
            )}
            aria-label={isTamil ? 'முதன்மை மெனு' : 'Main menu'}
          >
            {priorityMenuItems.map((item, index) => (
              <NavigationItem
                key={item.name}
                item={item}
                onClick={handleCloseMenu}
                itemVariants={itemVariants}
                ref={index === 0 ? firstFocusableRef : undefined}
              />
            ))}
            {secondaryMenuItems.length ? (
              <motion.div variants={itemVariants} className="w-full pt-1">
                <button
                  type="button"
                  onClick={handleToggleSecondaryMenu}
                  aria-expanded={showSecondaryMenu}
                  aria-controls="mobile-more-links"
                  className={cn(
                    'border-border/60 text-foreground hover:border-brand-peacock/60 hover:text-brand-peacock focus-visible:ring-ring focus-visible:ring-offset-background mx-auto inline-flex min-h-11 items-center gap-2 rounded-full border px-5 py-2 text-xs font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-offset-2',
                    isTamil ? 'tracking-normal' : 'tracking-[0.16em] uppercase'
                  )}
                >
                  <span>{isTamil ? 'மேலும் இணைப்புகள்' : 'More links'}</span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform duration-300 ease-out',
                      showSecondaryMenu ? 'rotate-180' : ''
                    )}
                    aria-hidden="true"
                  />
                </button>
                <AnimatePresence initial={false}>
                  {showSecondaryMenu ? (
                    <motion.div
                      id="mobile-more-links"
                      initial={{ opacity: 0, height: 0, y: -8 }}
                      animate={{ opacity: 1, height: 'auto', y: 0 }}
                      exit={{ opacity: 0, height: 0, y: -8 }}
                      transition={{ duration: 0.3, ease: easings.smooth }}
                      className="mt-4 w-full overflow-hidden"
                    >
                      <div
                        ref={moreLinksContainerRef}
                        className="border-border/55 bg-card/55 rounded-xl border px-4 py-5"
                      >
                        <div className="flex flex-col gap-4">
                          {secondaryMenuItems.map((item) => (
                            <NavigationItem
                              key={item.id}
                              item={item}
                              onClick={handleCloseMenu}
                              itemVariants={itemVariants}
                            />
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </motion.div>
            ) : null}
            <motion.div variants={itemVariants} className="pt-4">
              <Link
                to={sessionQuery.data?.user ? localizeHref('/account') : localizeHref('/account/sign-in')}
                onClick={handleCloseMenu}
                className={cn(
                  'border-border/60 text-foreground hover:border-brand-peacock/60 hover:text-brand-peacock focus-visible:ring-ring focus-visible:ring-offset-background inline-flex rounded-full border px-6 py-3 text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-offset-2',
                  isTamil ? 'tracking-normal' : 'tracking-[0.22em] uppercase'
                )}
              >
                {sessionQuery.data?.user ? (isTamil ? 'என் கணக்கு' : 'My Account') : isTamil ? 'உள்நுழை' : 'Sign In'}
              </Link>
            </motion.div>
            <motion.div variants={itemVariants} className="pt-2">
              <LanguageSwitcher />
            </motion.div>
            <motion.div variants={itemVariants} className="pt-8 sm:pt-10">
              <SocialLinks />
            </motion.div>
          </nav>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
